import axios from 'axios'

export type RdfFormat = 'turtle' | 'trig' | 'n3'

const CONTENT_TYPES: Record<RdfFormat, string> = {
  turtle: 'text/turtle',
  trig: 'application/trig',
  n3: 'text/n3',
}

const EXTENSIONS: Record<string, RdfFormat> = {
  ttl: 'turtle',
  turtle: 'turtle',
  trig: 'trig',
  n3: 'n3',
}

/**
 * Map a filename to the format the endpoint expects, for file pickers and
 * drag-and-drop. Returns null for anything the endpoint does not accept, so
 * callers can reject the file before uploading it.
 */
export function rdfFormatFromFilename(filename: string): RdfFormat | null {
  const extension = filename.split('.').pop()
  if (!extension || extension === filename) return null

  return EXTENSIONS[extension.toLowerCase()] ?? null
}

export type RdfUploadInput = {
  /**
   * Target graph. Turtle and N3 payloads land here in full. A TriG payload
   * only puts its default graph here: its named graphs go to the graphs they
   * name themselves.
   */
  graphUri: string
  format: RdfFormat
  /** The serialised RDF itself, as text or as a File/Blob straight from a file input. */
  content: string | Blob
  /** Aborts the request, e.g. when the user cancels or the component unmounts. */
  signal?: AbortSignal
}

/** Resolves to the graphs that were written, which for TriG can be more than the one requested. */
export type RequestRdfUpload = (input: RdfUploadInput) => Promise<string[]>

/**
 * POST an RDF payload to the Wistor rdf-upload endpoint.
 *
 * Curried on the URL, matching request-data-update: the URL is derived once
 * (see use-app-ctx) while the payload differs per call site.
 *
 * The endpoint reads the format from the Content-Type header, so the format is
 * passed explicitly rather than sniffed from the content. It answers 400 for a
 * payload that does not parse, 403 when the caller may not write one of the
 * target graphs, and 200 with a JSON array of the graphs it wrote.
 *
 * Aborting only stops this client from waiting: the server may already have
 * loaded part of a multi-graph TriG payload, so treat the target graphs as
 * being in an unknown state after an abort.
 */
export function requestRdfUpload(rdfUploadUrl: string): RequestRdfUpload {
  return async function requestRdfUpload({ graphUri, format, content, signal }: RdfUploadInput) {
    const response = await axios.post<string[]>(rdfUploadUrl, content, {
      params: { graphUri },
      headers: { 'Content-Type': CONTENT_TYPES[format] },
      withCredentials: true,
      signal,
    })

    return response.data
  }
}
